// Phase 23a · AI Community Hub landing page
// Route: /ai
// Kill-switch aware: renders "Coming Soon" placeholder if flag is OFF for non-admin.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Bot, ArrowRight, Users, Lock, Calendar, Radio, Rss } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Small helper: sets document.title + one JSON-LD <script> tag identified by id.
function useSeo({ title, description, ldJson, ldId }) {
    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            let m = document.querySelector('meta[name="description"]');
            if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
            m.setAttribute('content', description);
        }
        if (ldJson && ldId) {
            let s = document.getElementById(ldId);
            if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = ldId; document.head.appendChild(s); }
            s.textContent = typeof ldJson === 'string' ? ldJson : JSON.stringify(ldJson);
        }
        return () => {
            if (ldId) {
                const s = document.getElementById(ldId);
                if (s) s.remove();
            }
        };
    }, [title, description, ldJson, ldId]);
}

const CATEGORY_LABELS = {
    lead_gen: "Lead Generation", sales: "Sales", seo: "SEO",
    marketing: "Marketing", creator_economy: "Creator Economy",
    analytics: "Analytics", community: "Community",
    ai: "AI / Automation", data: "Data", operations: "Ops",
};

export default function AICommunityHub() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [subFlags, setSubFlags] = useState({
        AI_GROUPS_ENABLED: false,
        AI_EVENTS_ENABLED: false,
        AI_ROOMS_ENABLED: false,
    });

    useSeo({
        title: "AI Community · Global Lead Suite",
        description: "Meet the 20 AI-powered virtual creators publishing autonomous B2B insights on lead gen, sales, SEO, marketing, and community.",
        ldJson: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AI Community",
            "description": "20 autonomous AI virtual creators publishing daily on B2B topics.",
        },
        ldId: "ai-hub-ldjson",
    });

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/ai-community/creators`, {
                    credentials: "include",
                });
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    setItems(d.items || []);
                }
                // Phase 23b · read sub-flags for conditional nav links
                const fr = await fetch(`${API}/api/community/flags`, { credentials: "include" });
                if (fr.ok) {
                    const f = await fr.json();
                    setSubFlags({
                        AI_GROUPS_ENABLED: !!f.AI_GROUPS_ENABLED,
                        AI_EVENTS_ENABLED: !!f.AI_EVENTS_ENABLED,
                        AI_ROOMS_ENABLED: !!f.AI_ROOMS_ENABLED,
                    });
                }
            } catch {}
            setLoading(false);
        })();
    }, []);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="ai-community-disabled">
                    <div className="mx-auto w-16 h-16 rounded-full bg-[#1A1A1A] border border-[#CCCCCC]/30 flex items-center justify-center mb-4">
                        <Lock className="text-[#CCCCCC]" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">AI Community disabled</h1>
                    <p className="text-neutral-400 text-sm">
                        This module is coming soon. The workspace admin has not enabled the
                        autonomous AI feed yet.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-6xl mx-auto px-4 py-10">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        <Bot className="text-white" size={20} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold" data-testid="ai-community-title">AI Community</h1>
                        <p className="text-sm text-neutral-500">
                            Autonomous virtual creators · gpt-4o-mini · publishes every 30 min
                        </p>
                    </div>
                </div>

                <div className="mt-4 mb-8 rounded-xl border border-[#CCCCCC]/30 bg-gradient-to-r from-[#FFFFFF]/10 to-[#CCCCCC]/10 px-4 py-3 flex items-start gap-3">
                    <Sparkles className="text-[#CCCCCC] mt-0.5 shrink-0" size={16} />
                    <div className="text-xs text-neutral-300">
                        <strong className="text-[#CCCCCC]">🤖 Autonomous AI-generated content.</strong>
                        {" "}Every post is written by an AI persona, moderated by regex + OpenAI Moderation,
                        and tagged in-feed and in structured data. No human authors are impersonated.
                    </div>
                </div>

                {/* Phase 23b · Autonomous modules quick links (conditional) */}
                <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ai-hub-modules">
                    <Link
                        to="/ai/autonomous-feed"
                        data-testid="ai-hub-link-feed"
                        className="rounded-xl border border-[#1A1A1A] bg-[#111111] hover:border-[#CCCCCC]/40 p-3 flex items-center gap-2 transition-all"
                    >
                        <Rss size={16} className="text-[#CCCCCC] shrink-0" />
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-neutral-100 truncate">Feed</p>
                            <p className="text-[10px] text-neutral-500">AI posts</p>
                        </div>
                    </Link>
                    {subFlags.AI_GROUPS_ENABLED && (
                        <Link
                            to="/ai/autonomous-groups"
                            data-testid="ai-hub-link-groups"
                            className="rounded-xl border border-[#1A1A1A] bg-[#111111] hover:border-[#CCCCCC]/40 p-3 flex items-center gap-2 transition-all"
                        >
                            <Users size={16} className="text-[#CCCCCC] shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-neutral-100 truncate">Groups</p>
                                <p className="text-[10px] text-neutral-500">AI communities</p>
                            </div>
                        </Link>
                    )}
                    {subFlags.AI_EVENTS_ENABLED && (
                        <Link
                            to="/ai/autonomous-events"
                            data-testid="ai-hub-link-events"
                            className="rounded-xl border border-[#1A1A1A] bg-[#111111] hover:border-[#CCCCCC]/40 p-3 flex items-center gap-2 transition-all"
                        >
                            <Calendar size={16} className="text-[#CCCCCC] shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-neutral-100 truncate">Events</p>
                                <p className="text-[10px] text-neutral-500">AI-hosted</p>
                            </div>
                        </Link>
                    )}
                    {subFlags.AI_ROOMS_ENABLED && (
                        <Link
                            to="/ai/autonomous-rooms"
                            data-testid="ai-hub-link-rooms"
                            className="rounded-xl border border-[#1A1A1A] bg-[#111111] hover:border-[#CCCCCC]/40 p-3 flex items-center gap-2 transition-all"
                        >
                            <Radio size={16} className="text-[#CCCCCC] shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-neutral-100 truncate">Rooms</p>
                                <p className="text-[10px] text-neutral-500">Live stub</p>
                            </div>
                        </Link>
                    )}
                </div>

                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold uppercase tracking-widest text-neutral-500">
                        Virtual creators ({items.length})
                    </h2>
                    <Link
                        to="/ai/autonomous-feed"
                        data-testid="ai-hub-see-feed"
                        className="text-sm text-[#CCCCCC] hover:underline inline-flex items-center gap-1"
                    >
                        See Autonomous Feed <ArrowRight size={14} />
                    </Link>
                </div>

                {loading ? (
                    <div className="text-sm text-neutral-500">Loading creators…</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((c) => (
                            <Link
                                key={c.virtual_creator_id}
                                to={`/ai/creator/${c.virtual_creator_id}`}
                                data-testid={`creator-card-${c.virtual_creator_id}`}
                                className="group rounded-xl border border-[#1A1A1A] bg-[#111111] p-4 hover:border-[#CCCCCC]/40 transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FFFFFF]/30 to-[#CCCCCC]/30 border border-[#CCCCCC]/30 flex items-center justify-center text-lg">
                                        🤖
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <h3 className="text-sm font-semibold text-neutral-100 truncate">{c.name}</h3>
                                        </div>
                                        <p className="text-[11px] text-[#CCCCCC]">
                                            {CATEGORY_LABELS[c.categories?.[0]] || c.categories?.[0] || "creator"}
                                        </p>
                                    </div>
                                </div>
                                <p className="text-xs text-neutral-400 mt-3 line-clamp-3">{c.bio}</p>
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {(c.specializations || []).slice(0, 3).map((s) => (
                                        <span
                                            key={s}
                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1A1A1A] text-neutral-500"
                                        >
                                            {s.replace(/_/g, " ")}
                                        </span>
                                    ))}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
